import { Avatar, type AvatarProps } from "@msuite/ui/avatar";
import { retryLimit, useQuery } from "@msuite/ui/query";
import { Tooltip } from "@msuite/ui/tooltip";
import type { ClassNameProps } from "@msuite/ui/types";
import { responseHandler } from "@msuite/ui/utils";
import { getFullName } from "@msuite/utils/users";
import { client } from "@/hono";

/** Props Interface */
interface UserAvatarProps extends Pick<AvatarProps, "size">, ClassNameProps {
	userId: string | null;
	variant?: "image" | "initials";
	withTooltip?: boolean;
}

export function UserAvatar({
	userId,
	size = "xs",
	className,
	variant = "image",
	withTooltip = false,
}: UserAvatarProps) {
	/** Query */
	const { data } = useQuery({
		enabled: !!userId,
		queryFn: async () => {
			if (!userId) throw new Error("User does not exist");
			const response = await client.users[":userId"].$get({
				param: {
					userId,
				},
			});
			// No error toast: the users policy deliberately withholds a partner's name from a
			// caller without `read Partner`, and the route answers that as an absent row. A board
			// full of partner staff must degrade to placeholders, not greet the reader with a
			// toast per name.
			const data = await responseHandler(response, false);
			return data.user;
		},
		queryKey: ["users", userId],
		retry: retryLimit(3),
	});

	/** Render */
	return (
		<Tooltip
			isDisabled={!withTooltip}
			label={getFullName(data)}
		>
			<Avatar
				size={size}
				imageUrl={data?.image}
				className={className}
				initials={data?.initials}
				variant={variant}
			/>
		</Tooltip>
	);
}
